interface VitalReading {
  value: number;
  date: string;
  provider: string;
  location: string;
  device: string;
  type: "Doctor" | "Nurse" | "Pharmacist" | "Technician";
}

export const defaultRecentReadings: VitalReading[] = [
  {
    value: 68,
    date: "August 14th, 2025 at 18:24",
    provider: "Dr. Michael Chen",
    location: "Home Visit",
    device: "Digital Pulse Oximeter",
    type: "Pharmacist",
  },
  {
    value: 79,
    date: "August 5th, 2025 at 18:24",
    provider: "Dr. Michael Chen",
    location: "Consultation Room 2",
    device: "Digital Pulse Oximeter",
    type: "Pharmacist",
  },
  {
    value: 78,
    date: "July 28th, 2025 at 14:15",
    provider: "Dr. Sarah Wilson",
    location: "Clinic",
    device: "Automated BP Monitor",
    type: "Doctor",
  },
  {
    value: 75,
    date: "July 20th, 2025 at 09:30",
    provider: "Nurse Jennifer Adams",
    location: "Consultation Room 1",
    device: "Manual Stethoscope",
    type: "Nurse",
  },
  {
    value: 72,
    date: "July 12th, 2025 at 16:15",
    provider: "Dr. Michael Chen",
    location: "Emergency Room",
    device: "Digital Monitor",
    type: "Doctor",
  },
];

export const providerTypes = [
  { value: "Doctor", label: "Doctor", color: "bg-blue-100 text-blue-800" },
  { value: "Nurse", label: "Nurse", color: "bg-green-100 text-green-800" },
  {
    value: "Pharmacist",
    label: "Pharmacist",
    color: "bg-purple-100 text-purple-800",
  },
  {
    value: "Technician",
    label: "Technician",
    color: "bg-orange-100 text-orange-800",
  },
];

export const deviceTypes = [
  "Digital Pulse Oximeter",
  "Automated BP Monitor",
  "Manual Stethoscope",
  "Digital Monitor",
  "Wearable Device",
  "Smart Watch",
  "Home Monitor",
];

export const locationTypes = [
  "Home Visit",
  "Consultation Room 1",
  "Consultation Room 2",
  "Clinic",
  "Emergency Room",
  "Ward A",
  "Ward B",
  "Outpatient Department",
];

export const createVitalReading = (
  value: number,
  provider: string,
  location: string,
  device: string,
  type: VitalReading["type"] = "Doctor",
): VitalReading => ({
  value,
  date: new Date().toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  }),
  provider,
  location,
  device,
  type,
});

export const getProviderTypeColor = (type: string): string => {
  const providerType = providerTypes.find((pt) => pt.value === type);
  return providerType?.color || "bg-gray-100 text-gray-800";
};

export const generateMockRecentReadings = (
  latestValue: number,
  count: number = 5,
): VitalReading[] => {
  const readings: VitalReading[] = [];
  const providers = [
    "Dr. Michael Chen",
    "Dr. Sarah Wilson",
    "Nurse Jennifer Adams",
    "Dr. Robert Martinez",
  ];

  for (let i = 0; i < count; i++) {
    const variance = (Math.random() - 0.5) * 10; // ±5 variation
    const value = Math.max(50, Math.round(latestValue + variance));
    const daysAgo = i * 5 + Math.floor(Math.random() * 5);
    const date = new Date();
    date.setDate(date.getDate() - daysAgo);

    readings.push({
      value,
      date: date.toLocaleDateString("en-US", {
        year: "numeric",
        month: "long",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      }),
      provider: providers[Math.floor(Math.random() * providers.length)],
      location: locationTypes[Math.floor(Math.random() * locationTypes.length)],
      device: deviceTypes[Math.floor(Math.random() * deviceTypes.length)],
      type: ["Doctor", "Nurse", "Pharmacist"][
        Math.floor(Math.random() * 3)
      ] as VitalReading["type"],
    });
  }

  return readings;
};

export const getVitalColor = (type: string) => {
  switch (type) {
    case "heart-rate":
      return "#ef4444"; // red-500
    case "temperature":
      return "#f97316"; // orange-500
    case "blood-pressure":
      return "#8b5cf6"; // violet-500
    case "weight":
      return "#10b981"; // emerald-500
    case "oxygen-saturation":
      return "#06b6d4"; // cyan-500
    case "respiratory-rate":
      return "#22c55e"; // green-500
    case "news-pain-score":
      return "#f97316"; // orange-500
    case "blood-sugar":
      return "#e11d48"; // rose-500
    case "height":
      return "#3b82f6"; // blue-500
    case "circumference":
      return "#8b5cf6"; // violet-500
    case "bmi":
      return "#f59e0b";
    case "consciousness-level":
      return "#7c3aed"; // violet
    case "supplemental-oxygen":
      return "#0ea5e9"; // amber-500
    default:
      return "#6b7280"; // gray-500
  }
};
